import { defineStore } from 'pinia'
import { createStoreBase } from '@/presentation/stores/resources/storeBase.ts'
import EMethodos, { EMethodosInput } from '@/core/domain/emethodos/EMethodos.ts'
import { computed, type ComputedRef, ref } from 'vue'
import DependencyContainer from '@/presentation/configuration/DependencyContainer.ts'
import FetchAllEMethodosUseCase from '@/core/useCases/emethodos/FetchAllEMethodosUseCase.ts'
import { NabooError } from '@/core/infrastructure/services/NabooError.ts'
import UpdateEMethodosUseCase from '@/core/useCases/emethodos/UpdateEMethodosUseCase.ts'

export const useEMethodosStore = defineStore('emethodos', () => {
  // Utilisation de la base commune pour les stores
  const storeBase = createStoreBase(['fetchAllEMethodos', 'updateEMethodos'])

  // État
  const eMethodos = ref<EMethodos[]>([])

  // Actions
  async function fetchAllEMethodos() {
    try {
      // Indication du chargement
      storeBase.loading.value.fetchAllEMethodos = true
      storeBase.resetError()

      const result =
        await DependencyContainer.resolve<FetchAllEMethodosUseCase>(
          FetchAllEMethodosUseCase
        ).execute()

      eMethodos.value = result
      storeBase.updateLastUpdated()

      return result
    } catch (error) {
      handleError(error, `Une erreur est survenue lors de la récupération des e-methodos.`)
      return Promise.reject(error as Error)
    } finally {
      storeBase.loading.value.fetchAllEMethodos = false
    }
  }

  async function updateEMethodos(input: EMethodosInput) {
    try {
      // Indication du chargement
      storeBase.loading.value.updateEMethodos = true
      storeBase.resetError()

      const result =
        await DependencyContainer.resolve<UpdateEMethodosUseCase>(UpdateEMethodosUseCase).execute(
          input
        )

      eMethodos.value = eMethodos.value.map((item) => (item.id === result.id ? result : item))
      storeBase.updateLastUpdated()

      return result
    } catch (error) {
      handleError(error, `Une erreur est survenue lors de la mise à jour d'un e-methodos.`)
      return Promise.reject(error as Error)
    } finally {
      storeBase.loading.value.updateEMethodos = false
    }
  }

  // Computed
  const eMethodosCount: ComputedRef<number> = computed(() => eMethodos.value.length)

  // Helper pour gérer les erreurs
  function handleError(error: unknown, defaultMessage: string) {
    let nabooError: NabooError

    if (error instanceof NabooError) {
      nabooError = error
    } else if (error instanceof Error) {
      nabooError = new NabooError({
        message: defaultMessage,
        status: 500,
        name: 'EMethodosStoreError',
        code: 'EMETHODOS_STORE_ERROR',
        originalError: error
      })
    } else {
      nabooError = new NabooError({
        message: defaultMessage,
        status: 500,
        name: 'EMethodosStoreError',
        code: 'EMETHODOS_STORE_ERROR'
      })
    }

    storeBase.setError(nabooError)
  }

  // Reset du store
  function reset() {
    eMethodos.value = []
    storeBase.resetBase()
  }

  return {
    // État
    eMethodos,
    ...storeBase,
    eMethodosStoreError: storeBase.error,
    eMethodosStoreLoading: storeBase.loading,
    eMethodosStoreLastUpdated: storeBase.lastUpdated,

    // Actions
    fetchAllEMethodos,
    updateEMethodos,
    reset,

    // Getters
    eMethodosCount
  }
})
